import type { ReactNode } from 'react';

import { cn } from '@rocco/utils/cn';
import { CONTENT_CAP_PADDED } from './large-screen';
import { ReadMoreText } from './ReadMoreText';
import { RICH_TEXT_HTML_CLASS } from './rich-text';

export interface DetailSummaryRowProps {
    /** Small grey caption above the description, e.g. "Introducing {title}". */
    caption?: string;
    /** Extra classes for the caption (e.g. a page-specific font size). */
    captionClassName?: string;
    /** Rich-text (HTML) description. */
    descriptionHtml?: string;
    /** Plain-text / node description (used when `descriptionHtml` is absent). */
    description?: ReactNode;
    /**
     * Right column — price (product), professional credit (project) or a stat
     * (professional), plus CTA(s). Rendered in a right-aligned flex column.
     */
    aside: ReactNode;
    /**
     * Extra classes on the aside column — e.g. `md:top-0` to cancel the
     * default 24px upward pull when the aside must top-align with the caption.
     */
    asideClassName?: string;
    /**
     * `light` = ink on white (product). `dark` = light type on a transparent
     * background, for pages whose content block sits on a dark section (project).
     */
    theme?: 'light' | 'dark';
    /**
     * Clamp the description to this many lines with an overflow-aware
     * Read more toggle (HTML descriptions only). Unset = no clamp.
     */
    clampLines?: number;
    /**
     * With `clampLines`: Read more links to this in-page anchor (the full
     * description rendered elsewhere, e.g. the product-details block) instead
     * of expanding in place.
     */
    readMoreAnchor?: string;
    /**
     * Replaces the anchor's default scroll handling — for targets that need
     * more than a scroll (e.g. opening a collapsed accordion row first).
     */
    onReadMoreAnchorClick?: (event: React.MouseEvent) => void;
}

/**
 * Shared summary row under the detail-page hero: a description on the left and a
 * right-aligned meta + CTA column. Presentational — each page supplies the
 * `aside` (the product passes its price + state CTA + the ecom-modal trigger).
 */
export const DetailSummaryRow = ({
    caption,
    captionClassName,
    descriptionHtml,
    description,
    aside,
    asideClassName,
    theme = 'light',
    clampLines,
    readMoreAnchor,
    onReadMoreAnchorClick,
}: DetailSummaryRowProps) => {
    const isDark = theme === 'dark';
    const descriptionClass = cn(
        // Intro description is 33px (2.0625rem @1920) per the Figma product +
        // project intros (nodes 2354:55411 / 2354:55420), in a 966px column.
        // caption→description gap is 38px (2.375rem) — the left column's gap-[38px].
        'font-sans leading-[1.25] font-medium tracking-[-0.025em] max-md:mt-3 max-md:text-base md:mt-[2.375rem] md:max-w-[60.375rem] md:text-[2.0625rem]',
        isDark ? 'text-[#e5e5e5]' : 'text-[#181818]',
    );
    // Read more rests a step down from the description and darkens back to it
    // on hover, so it separates from the sentence it continues.
    const toggleColor = isDark
        ? 'text-[#a3a3a3] hover:text-[#e5e5e5]'
        : 'text-[#8f8f8f] hover:text-[#181818]';

    return (
        <section
            className={cn(
                'max-md:px-6 md:px-[3rem]',
                isDark ? 'bg-transparent' : 'bg-white',
            )}
        >
            <div
                className={cn(
                    CONTENT_CAP_PADDED,
                    'flex max-md:flex-col max-md:gap-6 md:gap-[7.2rem]',
                )}
            >
                <div className="min-w-0 flex-1">
                    {caption ? (
                        <p
                            className={cn(
                                // Caption: Figma intro "Client Name" — 15px,
                                // 500, tracking -0.05em (node 2177:25553).
                                'm-0 font-sans leading-[1.22] font-medium tracking-[-0.05em] max-md:text-sm md:text-[0.9375rem]',
                                isDark ? 'text-[#a3a3a3]' : 'text-[#767676]',
                                captionClassName,
                            )}
                        >
                            {caption}
                        </p>
                    ) : null}
                    {descriptionHtml && clampLines ? (
                        // No rich-text class here: the clamped teaser flattens
                        // its copy to plain text so the crop can slice it, so
                        // there are no headings, lists or links left for those
                        // rules to style. The unclamped branch below still
                        // renders the markup and keeps them.
                        <ReadMoreText
                            html={descriptionHtml}
                            lines={clampLines}
                            className={descriptionClass}
                            toggleClassName={toggleColor}
                            anchorHref={readMoreAnchor}
                            onAnchorClick={onReadMoreAnchorClick}
                        />
                    ) : descriptionHtml ? (
                        <div
                            className={`${descriptionClass} ${RICH_TEXT_HTML_CLASS}`}
                            dangerouslySetInnerHTML={{
                                __html: descriptionHtml,
                            }}
                        />
                    ) : typeof description === 'string' && clampLines ? (
                        <ReadMoreText
                            text={description}
                            lines={clampLines}
                            className={descriptionClass}
                            toggleClassName={toggleColor}
                            anchorHref={readMoreAnchor}
                            onAnchorClick={onReadMoreAnchorClick}
                        />
                    ) : description ? (
                        <div className={descriptionClass}>{description}</div>
                    ) : null}
                </div>

                <div
                    className={cn(
                        'flex shrink-0 flex-col items-end gap-[1.5rem] max-md:w-full max-md:gap-4 md:relative md:-top-6',
                        asideClassName,
                    )}
                >
                    {aside}
                </div>
            </div>
        </section>
    );
};
